import React from 'react';

import { useSelector } from 'react-redux';

import { useIntl } from 'src/hooks/intl';
import { getIsCurrentPullRequestOpen } from 'src/redux/pull-request/selectors';
import { getIsReviewInProgress } from 'src/redux/pull-request/selectors/review-selectors';
import { getConversationsMentionsContext } from 'src/selectors/conversation-selectors';
import { getIsAIEnabledForWorkspace } from 'src/selectors/feature-selectors';
import { getIsWorkspaceAdmin } from 'src/selectors/global-selectors';
import {
  getCurrentRepositoryOwnerName,
  getCurrentRepositorySlug,
} from 'src/selectors/repository-selectors';
import { getCurrentUserAccountId } from 'src/selectors/user-selectors';
import { getCurrentWorkspaceUUID } from 'src/selectors/workspace-selectors';

import { BBCAtlaskitEditor, EditorProps } from '../bbc-atlaskit-editor';
import { useAnalytics } from '../settings/analytics';

import { emojiProvider } from './providers/emoji-provider';
import { useLegacyImageUploadProvider } from './providers/legacy-image-upload-provider';
import { createMentionProvider } from './providers/mention-provider';

export type EditorWrapperProps = Pick<
  EditorProps,
  | 'defaultValue'
  | 'isExpanded'
  | 'onCancel'
  | 'onSave'
  | 'onClose'
  | 'onOpen'
  | 'isEditing'
  | 'onChange'
  | 'showBeforeUnloadWarning'
  | 'allowEmptyValue'
  | 'onEscapeHandler'
  | 'onEditorReady'
  | 'shouldFocus'
  | 'allowRule'
  | 'popupsMountPoint'
  | 'user'
  | 'placeholder'
  | 'disableScrollTo'
  | 'allowHelpButton'
  | 'draftLocalStorageKey'
  | 'multiCommentReview'
  | 'hideSecondaryToolbar'
  | 'publishStartReviewAnalytics'
  | 'summarisePrDescriptionUrl'
  | 'fileMeta'
>;

// This is the Frontbucket implementation of the BBCAtlaskitEditor component, which
// is Bitbucket's implementation of the Atlaskit Editor. This wrapper
// that provides the necessary props to make it work in Frontbucket's context.
// To actually make changes to the Editor functionality, you will need to
// modify the bbc-atlaskit-editor component.
export function Editor(props: EditorWrapperProps): JSX.Element {
  const intl = useIntl();
  const { publishButtonClickEvent } = useAnalytics({
    source: 'pullRequestScreen',
  });
  const owner = useSelector(getCurrentRepositoryOwnerName) || '';
  const slug = useSelector(getCurrentRepositorySlug) || '';
  const isAIEnabled = useSelector(getIsAIEnabledForWorkspace);
  const workspaceUuid = useSelector(getCurrentWorkspaceUUID);
  const currentUserAccountId = useSelector(getCurrentUserAccountId);

  const conversationsMentionContext = useSelector(
    getConversationsMentionsContext
  );
  const reviewInProgress = useSelector(getIsReviewInProgress);
  const pullRequestOpen = useSelector(getIsCurrentPullRequestOpen);
  const isWorkspaceAdmin = useSelector(getIsWorkspaceAdmin);
  const legacyImageUploadProvider = useLegacyImageUploadProvider();
  const mentionProvider = createMentionProvider({
    owner,
    slug,
    conversationsMentionContext,
    intl,
  });

  return (
    <BBCAtlaskitEditor
      intl={intl}
      conversationsMentionContext={conversationsMentionContext}
      {...props}
      owner={owner}
      repoSlug={slug}
      reviewInProgress={reviewInProgress}
      pullRequestOpen={pullRequestOpen}
      isAIEnabled={isAIEnabled}
      workspaceUuid={workspaceUuid}
      currentUserAccountId={currentUserAccountId}
      isWorkspaceAdmin={isWorkspaceAdmin}
      publishStartReviewAnalytics={publishButtonClickEvent}
      legacyImageUploadProvider={legacyImageUploadProvider}
      emojiProvider={emojiProvider}
      mentionProvider={mentionProvider}
    />
  );
}
